import { useState } from 'react';
import { useSubmittal } from '@/hooks/useSubmittals';
import { SubmittalItemsList } from './SubmittalItemsList';
import { SubmittalFileList } from './SubmittalFileList';
import { SubmittalRevisionHistory } from './SubmittalRevisionHistory';
import { SendSubmittalDialog } from './SendSubmittalDialog';
import { Loader2, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { PDFAnnotationViewer } from './PDFAnnotationViewer';
import { DocumentAnnotator } from '@/components/document-review/DocumentAnnotator';
import type { SubmittalFile } from '@/hooks/useSubmittals';

interface SubmittalDetailPanelProps {
  submittalId: string;
}

interface AnnotateState {
  file: SubmittalFile;
  pdfUrl: string;
  documentReviewId: string | null;
  reviewStatus: string | null;
}

export function SubmittalDetailPanel({ submittalId }: SubmittalDetailPanelProps) {
  const { data, isLoading } = useSubmittal(submittalId);

  // Annotation viewer state — documentReviewId is set when the document_review flag is ON
  const [annotateFile, setAnnotateFile] = useState<AnnotateState | null>(null);
  const [loadingAnnotateId, setLoadingAnnotateId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const { submittal, items, files, revisions } = data;

  /**
   * Open the annotation viewer for a PDF file.
   *
   * When the document_review feature flag is ON, the backend returns a
   * document_review_id for this submittal/file pair — we mount DocumentAnnotator.
   * When the flag is OFF (403) or the call fails, we fall back to PDFAnnotationViewer.
   */
  const handleAnnotateFile = async (file: SubmittalFile) => {
    if (loadingAnnotateId) return;
    setLoadingAnnotateId(file.id);

    try {
      const token = await import('firebase/auth').then(({ getAuth }) => {
        const u = getAuth().currentUser;
        return u ? u.getIdToken() : Promise.reject(new Error('Not authenticated'));
      });

      const apiBase = import.meta.env.VITE_API_URL;
      const downloadResponse = await fetch(
        `${apiBase}/api/submittals/${submittalId}/files/${file.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!downloadResponse.ok) throw new Error('Failed to load file for annotation');

      const blob = await downloadResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Try the document_review proxy — succeeds only when flag is ON
      let documentReviewId: string | null = null;
      let reviewStatus: string | null = null;
      try {
        const drResponse = await api.documentReviews.forSubmittal(submittalId, blobUrl);
        if (drResponse?.review?.id) {
          documentReviewId = drResponse.review.id as string;
          reviewStatus = drResponse.review.status as string ?? null;
        }
      } catch {
        // Flag off (403) or any error → fall back to PDFAnnotationViewer
      }

      setAnnotateFile({ file, pdfUrl: blobUrl, documentReviewId, reviewStatus });
    } catch (err: any) {
      console.error('Annotation viewer open failed:', err);
    } finally {
      setLoadingAnnotateId(null);
    }
  };

  const handleCloseAnnotateViewer = () => {
    if (annotateFile?.pdfUrl) {
      URL.revokeObjectURL(annotateFile.pdfUrl);
    }
    setAnnotateFile(null);
  };

  return (
    <div className="px-4 py-3 bg-muted/30 border-t space-y-4">
      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => api.submittals.downloadCoverSheet(submittalId, submittal.submittal_number)}
        >
          <Download className="w-3 h-3 mr-1" />
          Cover Sheet
        </Button>
        <SendSubmittalDialog
          submittalId={submittalId}
          submittalNumber={submittal.submittal_number}
          defaultRecipient={submittal.submitted_to_email || undefined}
          itemCount={items.length}
          fileCount={files.length}
        />
      </div>

      {/* Description */}
      {submittal.description && (
        <div>
          <span className="text-xs font-medium text-muted-foreground">Description</span>
          <p className="text-sm mt-1">{submittal.description}</p>
        </div>
      )}

      {/* Metadata row */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {submittal.spec_section && (
          <div>
            <span className="text-xs text-muted-foreground">Spec Section: </span>
            <span>{submittal.spec_section}</span>
          </div>
        )}
        {submittal.submitted_to && (
          <div>
            <span className="text-xs text-muted-foreground">To: </span>
            <span>{submittal.submitted_to}</span>
          </div>
        )}
        {submittal.submitted_to_email && (
          <div>
            <span className="text-xs text-muted-foreground">Email: </span>
            <span>{submittal.submitted_to_email}</span>
          </div>
        )}
        {submittal.date_required && (
          <div>
            <span className="text-xs text-muted-foreground">Required: </span>
            <span>{new Date(submittal.date_required).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Items */}
      <SubmittalItemsList submittalId={submittalId} items={items} />

      {/* Files — with Annotate button for PDFs */}
      <div>
        <span className="text-xs font-medium text-muted-foreground block mb-2">Files</span>
        <SubmittalFileList
          submittalId={submittalId}
          files={files}
          onAnnotateFile={handleAnnotateFile}
          annotatingFileId={loadingAnnotateId}
        />
      </div>

      {/* Revisions */}
      <SubmittalRevisionHistory revisions={revisions} />

      {/* Annotation viewer modal — DocumentAnnotator when flag ON, PDFAnnotationViewer when flag OFF */}
      <Dialog open={!!annotateFile} onOpenChange={(open) => { if (!open) handleCloseAnnotateViewer(); }}>
        {/* [&>button]:hidden suppresses the default Radix absolute X button which
            overlaps the annotation toolbar at the top-right of the dialog */}
        <DialogContent className="max-w-5xl w-full h-[90vh] flex flex-col p-0 gap-0 overflow-hidden [&>button]:hidden">
          {/* Visible title header — shows which file is being annotated */}
          <div className="flex items-center justify-between px-3 py-2 border-b bg-background shrink-0">
            <DialogTitle className="text-sm font-medium truncate">
              Annotating: {annotateFile?.file.file_name}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 ml-2 shrink-0"
              onClick={handleCloseAnnotateViewer}
            >
              <X className="w-3.5 h-3.5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
          {annotateFile && annotateFile.documentReviewId ? (
            <DocumentAnnotator
              documentReviewId={annotateFile.documentReviewId}
              pdfUrl={annotateFile.pdfUrl}
              mode="approve"
              reviewStatus={annotateFile.reviewStatus ?? undefined}
              onClose={handleCloseAnnotateViewer}
            />
          ) : annotateFile ? (
            <PDFAnnotationViewer
              submittalId={submittalId}
              fileId={annotateFile.file.id}
              fileName={annotateFile.file.file_name}
              projectId={submittal.project_id}
              submittalNumber={submittal.submittal_number}
              pdfUrl={annotateFile.pdfUrl}
              onClose={handleCloseAnnotateViewer}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
